import { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Copy, Check } from "lucide-react";
import { useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import ListPagination from "../../containers/ListPagination";
import { useListPagination } from "../../../hooks/useListPagination";
import { getCoupons, createCoupon } from "../../../server/Coupon";
import { getAllCustomer } from "../../../server/Customer";
import "./Coupons.scss";
import showAlert from "../../../server/ShowAlert";

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [_searchTerm, _setSearchTerm] = useState("");
  const [_filterStatus, _setFilterStatus] = useState("all");
  const [copiedCode, setCopiedCode] = useState(null);

  const { stationId } = useSelector((state) => ({
    stationId: state.user.stationId,
  }));

  const loadCouponsAndCustomers = useCallback(() => {
    getCoupons()
      .then((res) => {
        setCoupons(res.reverse());
      })
      .catch((e) => console.log(e));
    getAllCustomer(stationId)
      .then((x) => setCustomers(x.data))
      .catch(() => console.log("error"));
  }, [stationId]);

  useEffect(() => {
    loadCouponsAndCustomers();
  }, [loadCouponsAndCustomers]);

  const saveCoupon = (couponData) => {
    const newCoupon = {
      Sum: couponData.amount,
      MinOrder: couponData.minOrder,
      ExpiredDate: couponData.expiryDate,
      EmailAddress: couponData.linkedCustomerId,
    };

    createCoupon(newCoupon)
      .then((res) => {
        showAlert({ msgTitle: "הקופון נוצר בהצלחה!", msgType: "success" });
        setCoupons((prev) => [res.data, ...prev]);
      })
      .catch((e) => {
        showAlert({ msgTitle: "אירעה שגיאה ביצירת הקופון", msgType: "error" });
        console.log(e);
      });
    setShowCreateForm(false);
  };

  const deleteCoupon = (couponId) => {
    if (window.confirm("האם אתה בטוח שברצונך למחוק את הקופון?")) {
      setCoupons((prev) => prev.filter((coupon) => coupon.id !== couponId));
    }
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const {
    toShow,
    pCounter,
    cFrom,
    cTo,
    isRowVisible,
    showPagination,
    showPaggination,
    UpPaggination,
    downPaggination,
    toShowFun,
  } = useListPagination(coupons.length);

  return (
    <div className="coupon-manager">
      <div className="header">
        <h1>ניהול קופונים</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus size={16} />
          צור קופון חדש
        </button>
      </div>
      <div className="header-spacer" aria-hidden="true" />

      <ListPagination
        className="coupons-pagination"
        showPagination={showPagination}
        toShow={toShow}
        toShowFun={toShowFun}
        cFrom={cFrom}
        cTo={cTo}
        pCounter={pCounter}
        showPaggination={showPaggination}
        downPaggination={downPaggination}
        UpPaggination={UpPaggination}
      />

      <div className="coupons-table">
        <div className="coupons-table-scroll">
        <table>
          <thead>
            <tr>
              <th>קוד קופון</th>
              <th>סכום</th>
              <th>למימוש החל מ-</th>
              <th>תוקף עד</th>
              <th>סטטוס</th>
              <th>מייל לקוח מקושר</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {coupons?.map((coupon, index) =>
              isRowVisible(index) ? (
              <tr key={coupon.id}>
                <td>
                  <div className="coupon-code">
                    <span className="code">{coupon.CouponCode}</span>
                    <button
                      className="copy-btn"
                      onClick={() => copyCouponCode(coupon.CouponCode)}
                      title="העתק קוד"
                    >
                      {copiedCode === coupon.CouponCode ? (
                        <Check size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                </td>
                <td>{coupon.Sum} ש"ח</td>
                <td>{coupon.MinOrder} ש"ח</td>
                <td>{coupon.ExpiredDate.split("T")[0]}</td>
                <td>
                  <span className={`status ${coupon.Active}`}>
                    {coupon.Active ? "פעיל" : "לא פעיל"}
                  </span>
                </td>
                <td>{coupon.EmailAddress}</td>
                <td>
                  <div className="actions">
                    <button
                      className="btn-icon"
                      onClick={() => setEditingCoupon(coupon)}
                      title="עריכה"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => deleteCoupon(coupon.id)}
                      title="מחיקה"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ) : null,
            )}
          </tbody>
        </table>
        </div>
      </div>

      {(showCreateForm || editingCoupon) && (
        <PopUp
          close={() => {
            setShowCreateForm(false);
            setEditingCoupon(null);
          }}
          width={700}
        >
          <CouponFormModal
            coupon={editingCoupon}
            customers={customers}
            onSave={
              editingCoupon
                ? (data) => {
                    setCoupons((prev) =>
                      prev.map((c) =>
                        c.id === editingCoupon.id ? { ...c, ...data } : c,
                      ),
                    );
                    setEditingCoupon(null);
                  }
                : saveCoupon
            }
            onClose={() => {
              setShowCreateForm(false);
              setEditingCoupon(null);
            }}
          />
        </PopUp>
      )}
    </div>
  );
};

const CouponFormModal = ({ coupon, customers, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    amount: coupon?.amount || "",
    minOrder: coupon?.minOrder || "",
    expiryDate: coupon?.expiryDate || "",
    linkedCustomerId: coupon?.linkedCustomerId || "",
    status: coupon?.status || "לא מומש",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.expiryDate || !formData.minOrder) {
      showAlert({
        msgTitle: "אנא מלא את כל השדות המסומנים בכוכבית",
        msgType: "error",
      });
      return;
    }
    onSave(formData);
  };

  return (
    <div className="coupon-modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{coupon ? "עריכת קופון" : "יצירת קופון חדש"}</h2>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label>סכום הקופון *</label>
            <input
              type="number"
              min="1"
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
              placeholder="הכנס סכום בש״ח"
              required
            />
          </div>
          <div className="form-group">
            <label>מינימום לקנייה *</label>
            <input
              type="number"
              min="1"
              value={formData.minOrder}
              onChange={(e) =>
                setFormData({ ...formData, minOrder: e.target.value })
              }
              placeholder="הכנס סכום בש״ח"
              required
            />
          </div>
          <div className="form-group">
            <label>תוקף עד *</label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) =>
                setFormData({ ...formData, expiryDate: e.target.value })
              }
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>

          <div className="form-group">
            <label>קישור ללקוח</label>
            <select
              value={formData.linkedCustomerId}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  linkedCustomerId: e.target.value ? e.target.value : null,
                })
              }
            >
              <option value="">בחר</option>
              {customers.map((customer) => (
                <option key={customer.email} value={customer.email}>
                  {customer.firstName} {customer.lastName}
                </option>
              ))}
            </select>
          </div>

          {coupon && (
            <div className="form-group">
              <label>סטטוס</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="לא מומש">לא מומש</option>
                <option value="מומש">מומש</option>
                <option value="פג תוקף">פג תוקף</option>
              </select>
            </div>
          )}

          <div className="coupons-modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              ביטול
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleSubmit}
            >
              {coupon ? "עדכן" : "צור קופון"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coupons;
